import type { UnknownAction } from '@reduxjs/toolkit';
import type { RootState } from 'src/store';
import { startAppListening } from 'src/store/middleware/listenerMiddleware';

/** Configuration for {@link createReopenCatchUp}. */
export interface ReopenCatchUpOptions {
  /** The invalidations that catch the channel's caches up after a reopen. */
  catchUp: () => UnknownAction[];
  /**
   * Matches the action that clears the caches {@link catchUp} refreshes, e.g.
   * `someApi.util.resetApiState.match`.
   */
  isCacheReset: (action: UnknownAction) => boolean;
  /** Whether the channel's socket is open right now. */
  isOpen: (state: RootState) => boolean;
}

/** Catch-up state for one WS channel, from {@link createReopenCatchUp}. */
export interface ReopenCatchUp {
  /**
   * Call from the channel's open handler, after authenticating, for every
   * open the channel keeps (not for a socket it drops). Dispatches the
   * catch-up unless this is the first open for the current caches.
   */
  onOpen: (dispatch: (action: UnknownAction) => unknown) => void;
  /** Starts tracking cache resets; returns the unsubscribe. */
  startListening: () => () => void;
  /** Test helper: forgets every earlier open. */
  reset: () => void;
}

/**
 * Refetches a WS channel's caches whenever its socket opens again: the
 * `Invalidate` frames sent while it was closed are lost, and a query still
 * cached from before is reused without a refetch.
 *
 * The first open for a set of caches is skipped — the pages' queries have just
 * fetched on mount. That leaves one narrow gap: a change committed between a
 * query's response and that first open is not caught up until the next
 * reopen, or the channel's polling fallback.
 *
 * "First" is tracked per cache lifetime, not per page load: a cache reset
 * (sign-out, tenant switch) while the socket is closed makes the next open a
 * first one again. A reset while the socket stays open does not — queries
 * refetched after it ran with the socket up, so a later reopen still has
 * frames to catch up on.
 */
export const createReopenCatchUp = ({
  catchUp,
  isCacheReset,
  isOpen,
}: ReopenCatchUpOptions): ReopenCatchUp => {
  /** Whether the socket has opened since the caches were last reset. */
  let hasOpened = false;

  return {
    onOpen: (dispatch) => {
      if (hasOpened) {
        for (const action of catchUp()) {
          dispatch(action);
        }
      }

      hasOpened = true;
    },
    startListening: () =>
      startAppListening({
        predicate: (action) => isCacheReset(action),
        effect: (_action, listenerApi) => {
          hasOpened = isOpen(listenerApi.getState());
        },
      }),
    reset: () => {
      hasOpened = false;
    },
  };
};
